/**
 * Klient WebSocket z reconnectem (PLAN.md §9).
 *
 * Jedno połączenie na kartę przeglądarki, dzielone przez wszystkie komponenty.
 * Autoryzacja jedzie httponly ciasteczkiem `auth_token` (ten sam origin), więc
 * nie doklejamy tokenu do URL-a.
 *
 * Serwer -> klient: chat.message (start/delta/final), agent.action, data.changed, error, ready.
 * Klient -> serwer: chat.send, action.confirm/reject/undo, space.switch, ping.
 */

export type WsStatus = "connecting" | "open" | "closed";

export interface ServerEvent {
  type: string;
  [key: string]: unknown;
}

type Listener = (event: ServerEvent) => void;
type StatusListener = () => void;

const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 25_000;

function socketUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws`;
}

class WsClient {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private status: WsStatus = "closed";
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  /** Wysyłki zakolejkowane, gdy socket akurat wstaje. */
  private queue: unknown[] = [];

  getStatus = (): WsStatus => this.status;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    this.connect();
    return () => this.listeners.delete(listener);
  }

  subscribeStatus = (listener: StatusListener): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  send(message: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.queue.push(message);
      this.connect();
    }
  }

  connect(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    this.setStatus("connecting");

    const socket = new WebSocket(socketUrl());
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus("open");
      for (const message of this.queue.splice(0)) socket.send(JSON.stringify(message));
      this.pingTimer = window.setInterval(() => this.send({ type: "ping" }), PING_INTERVAL_MS);
    };

    socket.onmessage = (raw) => {
      let event: ServerEvent;
      try {
        event = JSON.parse(raw.data as string) as ServerEvent;
      } catch {
        return;
      }
      for (const listener of this.listeners) listener(event);
    };

    socket.onclose = () => {
      this.clearPing();
      this.socket = null;
      this.setStatus("closed");
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  /** Wywoływane po zalogowaniu/wylogowaniu, żeby nie trzymać martwego socketu. */
  reset(): void {
    this.clearPing();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.attempt = 0;
    this.socket?.close();
    this.socket = null;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.listeners.size === 0) return;
    const delay = Math.min(1000 * 2 ** this.attempt, MAX_BACKOFF_MS);
    this.attempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearPing(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private setStatus(status: WsStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener();
  }
}

export const wsClient = new WsClient();
